import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Badge,
  InputAdornment,
} from "@mui/material";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import ScienceIcon from "@mui/icons-material/Science";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import PrintIcon from "@mui/icons-material/Print";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const MedicationTrackerPage = () => {
  const [medications, setMedications] = useState([
    {
      id: 1,
      name: "Tafamidis",
      genericName: "Tafamidis meglumine",
      dosage: "61 mg",
      frequency: "Once daily",
      timeOfDay: ["09:00"],
      purpose: "ATTR-CM treatment",
      prescribedBy: "Dr. Smith",
      startDate: "2024-01-15",
      active: true,
      reminders: true,
      withFood: true,
      trialExclusion: false,
      sideEffects: [],
    },
    {
      id: 2,
      name: "Furosemide",
      genericName: "Furosemide",
      dosage: "40 mg",
      frequency: "Twice daily",
      timeOfDay: ["08:00", "16:00"],
      purpose: "Diuretic for fluid management",
      prescribedBy: "Dr. Smith",
      startDate: "2023-11-01",
      active: true,
      reminders: true,
      withFood: false,
      trialExclusion: false,
      sideEffects: [],
    },
    {
      id: 3,
      name: "Bortezomib",
      genericName: "Bortezomib",
      dosage: "1.3 mg/m²",
      frequency: "Twice weekly",
      timeOfDay: ["10:00"],
      purpose: "AL amyloidosis chemotherapy",
      prescribedBy: "Dr. Johnson",
      startDate: "2024-06-01",
      endDate: "2024-10-01",
      active: false,
      reminders: false,
      withFood: false,
      trialExclusion: true,
      sideEffects: ["Fatigue", "Nausea"],
    },
  ]);

  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [interactionsDialogOpen, setInteractionsDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    genericName: "",
    dosage: "",
    frequency: "",
    timeOfDay: [],
    purpose: "",
    prescribedBy: "",
    startDate: "",
    endDate: "",
    active: true,
    reminders: true,
    withFood: false,
    notes: "",
  });

  // Mock drug interactions database
  const drugInteractions = [
    {
      drug1: "Tafamidis",
      drug2: "Warfarin",
      severity: "moderate",
      description: "May increase bleeding risk. Monitor INR closely.",
    },
    {
      drug1: "Furosemide",
      drug2: "Digoxin",
      severity: "moderate",
      description:
        "Furosemide can cause potassium loss, increasing digoxin toxicity risk.",
    },
    {
      drug1: "Bortezomib",
      drug2: "Ketoconazole",
      severity: "major",
      description: "Strong CYP3A4 inhibitors may increase bortezomib levels.",
    },
  ];

  // Mock trial exclusion medications
  const trialExclusionMeds = [
    "Bortezomib",
    "Daratumumab",
    "Cyclophosphamide",
    "Melphalan",
    "Dexamethasone (high dose)",
  ];

  const frequencies = [
    "Once daily",
    "Twice daily",
    "Three times daily",
    "Four times daily",
    "Every other day",
    "Twice weekly",
    "Weekly",
    "As needed",
  ];

  const handleDialogOpen = (medication = null) => {
    if (medication) {
      setEditingMedication(medication);
      setFormData(medication);
    } else {
      setEditingMedication(null);
      setFormData({
        name: "",
        genericName: "",
        dosage: "",
        frequency: "",
        timeOfDay: [],
        purpose: "",
        prescribedBy: "",
        startDate: "",
        endDate: "",
        active: true,
        reminders: true,
        withFood: false,
        notes: "",
      });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingMedication(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveMedication = () => {
    if (!formData.name || !formData.dosage || !formData.frequency) {
      alert("Please fill in required fields: Name, Dosage, and Frequency");
      return;
    }

    // Check if medication is on trial exclusion list
    const isExcluded = trialExclusionMeds.some((med) =>
      formData.name.toLowerCase().includes(med.toLowerCase())
    );

    if (editingMedication) {
      setMedications((prev) =>
        prev.map((med) =>
          med.id === editingMedication.id
            ? {
                ...formData,
                id: med.id,
                trialExclusion: isExcluded,
                sideEffects: med.sideEffects,
              }
            : med
        )
      );
    } else {
      const newMedication = {
        ...formData,
        id: Math.max(...medications.map((m) => m.id), 0) + 1,
        trialExclusion: isExcluded,
        sideEffects: [],
      };
      setMedications((prev) => [...prev, newMedication]);
    }

    handleDialogClose();
  };

  const handleDeleteMedication = (id) => {
    if (window.confirm("Are you sure you want to delete this medication?")) {
      setMedications((prev) => prev.filter((med) => med.id !== id));
    }
  };

  const handleToggleActive = (id) => {
    setMedications((prev) =>
      prev.map((med) => (med.id === id ? { ...med, active: !med.active } : med))
    );
  };

  const checkInteractions = () => {
    const activeMeds = medications.filter((m) => m.active);
    const foundInteractions = [];

    for (let i = 0; i < activeMeds.length; i++) {
      for (let j = i + 1; j < activeMeds.length; j++) {
        const interaction = drugInteractions.find(
          (inter) =>
            (inter.drug1 === activeMeds[i].name &&
              inter.drug2 === activeMeds[j].name) ||
            (inter.drug2 === activeMeds[i].name &&
              inter.drug1 === activeMeds[j].name)
        );

        if (interaction) {
          foundInteractions.push({
            ...interaction,
            medications: [activeMeds[i].name, activeMeds[j].name],
          });
        }
      }
    }

    return foundInteractions;
  };

  const interactions = checkInteractions();
  const activeMedications = medications.filter((m) => m.active);
  const discontinuedMedications = medications.filter((m) => !m.active);
  const trialExclusionCount = activeMedications.filter(
    (m) => m.trialExclusion
  ).length;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "major":
        return "error";
      case "moderate":
        return "warning";
      case "minor":
        return "info";
      default:
        return "default";
    }
  };

  const getTodaySchedule = () => {
    const schedule = [];
    activeMedications.forEach((med) => {
      med.timeOfDay?.forEach((time) => {
        schedule.push({
          time,
          medication: med.name,
          dosage: med.dosage,
          withFood: med.withFood,
          reminders: med.reminders,
        });
      });
    });
    return schedule.sort((a, b) => a.time.localeCompare(b.time));
  };

  const todaySchedule = getTodaySchedule();

  const handlePrintList = () => {
    window.print();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <LocalPharmacyIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Medication Tracker
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Track medications, check interactions, and manage your treatment plan
        </Typography>
      </Box>

      {/* Alert Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {interactions.length > 0 && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setInteractionsDialogOpen(true)}
                >
                  View Details
                </Button>
              }
            >
              <Typography variant="subtitle2" gutterBottom>
                <strong>
                  {interactions.length} Drug Interaction(s) Detected
                </strong>
              </Typography>
              <Typography variant="body2">
                Review potential interactions between your current medications.
              </Typography>
            </Alert>
          </Grid>
        )}

        {trialExclusionCount > 0 && (
          <Grid item xs={12}>
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Trial Participation Note</strong>
              </Typography>
              <Typography variant="body2">
                {trialExclusionCount} of your medications may affect clinical
                trial eligibility. Discuss with your doctor if considering trial
                participation.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column - Stats & Schedule */}
        <Grid item xs={12} md={4}>
          {/* Add Medication Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen()}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              py: 1.5,
              mb: 2,
            }}
          >
            Add Medication
          </Button>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {activeMedications.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Medications
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card elevation={2}>
                <CardContent>
                  <Badge badgeContent={interactions.length} color="error">
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color="warning.main"
                    >
                      {interactions.length}
                    </Typography>
                  </Badge>
                  <Typography variant="body2" color="text.secondary">
                    Interactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Today's Schedule */}
          <Card elevation={2}>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <ScheduleIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Today's Schedule
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {todaySchedule.length > 0 ? (
                <List dense>
                  {todaySchedule.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={item.time}
                              size="small"
                              color="primary"
                              icon={<ScheduleIcon />}
                            />
                            {item.reminders && (
                              <NotificationsActiveIcon
                                fontSize="small"
                                color="action"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {item.medication} - {item.dosage}
                            </Typography>
                            {item.withFood && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Take with food
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ py: 2 }}
                >
                  No scheduled medications for today
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintList}
            >
              Print List
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => alert("Export feature coming soon!")}
            >
              Export
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Medications List */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3}>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Active
                    <Chip label={activeMedications.length} size="small" />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Discontinued
                    <Chip label={discontinuedMedications.length} size="small" />
                  </Box>
                }
              />
              <Tab label="All Medications" />
            </Tabs>

            {/* Medications Table */}
            <Box sx={{ p: 2 }}>
              {(activeTab === 0
                ? activeMedications
                : activeTab === 1
                ? discontinuedMedications
                : medications
              ).length > 0 ? (
                <Box>
                  {(activeTab === 0
                    ? activeMedications
                    : activeTab === 1
                    ? discontinuedMedications
                    : medications
                  ).map((med) => (
                    <Accordion key={med.id} elevation={1} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%",
                            pr: 2,
                          }}
                        >
                          <VaccinesIcon
                            color={med.active ? "primary" : "disabled"}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight="bold">
                              {med.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {med.dosage} • {med.frequency}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            {med.trialExclusion && (
                              <Tooltip title="May affect trial eligibility">
                                <Chip
                                  label="Trial Alert"
                                  size="small"
                                  color="warning"
                                  icon={<WarningIcon />}
                                />
                              </Tooltip>
                            )}
                            {med.reminders && med.active && (
                              <Tooltip title="Reminders enabled">
                                <NotificationsActiveIcon
                                  fontSize="small"
                                  color="primary"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Generic Name
                            </Typography>
                            <Typography variant="body2">
                              {med.genericName || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Purpose
                            </Typography>
                            <Typography variant="body2">
                              {med.purpose}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Prescribed By
                            </Typography>
                            <Typography variant="body2">
                              {med.prescribedBy || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Start Date
                            </Typography>
                            <Typography variant="body2">
                              {new Date(med.startDate).toLocaleDateString()}
                            </Typography>
                          </Grid>

                          {med.timeOfDay && med.timeOfDay.length > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Schedule
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  mt: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {med.timeOfDay.map((time, idx) => (
                                  <Chip
                                    key={idx}
                                    label={time}
                                    size="small"
                                    icon={<ScheduleIcon />}
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}

                          {med.withFood && (
                            <Grid item xs={12}>
                              <Alert severity="info" icon={<InfoIcon />}>
                                <Typography variant="caption">
                                  Take with food
                                </Typography>
                              </Alert>
                            </Grid>
                          )}

                          {med.sideEffects && med.sideEffects.length > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Reported Side Effects
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  mt: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {med.sideEffects.map((effect, idx) => (
                                  <Chip
                                    key={idx}
                                    label={effect}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => handleDialogOpen(med)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color={med.active ? "warning" : "success"}
                                onClick={() => handleToggleActive(med.id)}
                              >
                                {med.active ? "Discontinue" : "Reactivate"}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteMedication(med.id)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <LocalPharmacyIcon
                    sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No medications found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {activeTab === 0
                      ? "Add your first active medication"
                      : activeTab === 1
                      ? "No discontinued medications"
                      : "Start tracking your medications"}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleDialogOpen()}
                    sx={{
                      background:
                        "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    }}
                  >
                    Add Medication
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Medication Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMedication ? "Edit Medication" : "Add New Medication"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Medication Name"
                required
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Tafamidis"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Generic Name"
                value={formData.genericName}
                onChange={(e) =>
                  handleInputChange("genericName", e.target.value)
                }
                placeholder="e.g., Tafamidis meglumine"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dosage"
                required
                value={formData.dosage}
                onChange={(e) => handleInputChange("dosage", e.target.value)}
                placeholder="e.g., 61 mg"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency"
                  onChange={(e) =>
                    handleInputChange("frequency", e.target.value)
                  }
                >
                  {frequencies.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose / Indication"
                value={formData.purpose}
                onChange={(e) => handleInputChange("purpose", e.target.value)}
                placeholder="e.g., ATTR-CM treatment"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prescribed By"
                value={formData.prescribedBy}
                onChange={(e) =>
                  handleInputChange("prescribedBy", e.target.value)
                }
                placeholder="e.g., Dr. Smith"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Daily Schedule (comma-separated times)"
                value={formData.timeOfDay?.join(", ") || ""}
                onChange={(e) =>
                  handleInputChange(
                    "timeOfDay",
                    e.target.value
                      .split(",")
                      .map((t) => t.trim())
                      .filter((t) => t)
                  )
                }
                placeholder="e.g., 09:00, 21:00"
                helperText="Enter times in 24-hour format (HH:MM)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reminders}
                    onChange={(e) =>
                      handleInputChange("reminders", e.target.checked)
                    }
                  />
                }
                label="Enable medication reminders"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.withFood}
                    onChange={(e) =>
                      handleInputChange("withFood", e.target.checked)
                    }
                  />
                }
                label="Take with food"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Add any additional notes or instructions..."
              />
            </Grid>

            {trialExclusionMeds.some((med) =>
              formData.name.toLowerCase().includes(med.toLowerCase())
            ) && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningIcon />}>
                  <Typography variant="body2">
                    <strong>Trial Participation Alert:</strong> This medication
                    may affect eligibility for certain clinical trials. Consult
                    with your healthcare provider if considering trial
                    participation.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSaveMedication}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            {editingMedication ? "Update" : "Add"} Medication
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drug Interactions Dialog */}
      <Dialog
        open={interactionsDialogOpen}
        onClose={() => setInteractionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            Drug Interactions
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              The following potential drug interactions have been identified
              based on your current medications. Always consult with your
              healthcare provider or pharmacist about medication interactions.
            </Typography>
          </Alert>

          {interactions.map((interaction, index) => (
            <Card key={index} elevation={2} sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Chip
                    label={interaction.severity.toUpperCase()}
                    color={getSeverityColor(interaction.severity)}
                    size="small"
                  />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {interaction.medications.join(" + ")}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {interaction.description}
                </Typography>
              </CardContent>
            </Card>
          ))}

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="caption">
              <strong>Disclaimer:</strong> This interaction checker is for
              informational purposes only and does not replace professional
              medical advice. Always consult with your healthcare provider or
              pharmacist before making any changes to your medications.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInteractionsDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={() => alert("Print feature coming soon!")}
            startIcon={<PrintIcon />}
          >
            Print Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Medical Disclaimer:</strong> This medication tracker is for
          informational and organizational purposes only. It does not provide
          medical advice. Drug interaction information is based on known
          interactions but may not be comprehensive. Always consult with your
          healthcare provider or pharmacist regarding your medications, dosages,
          and potential interactions.
        </Typography>
      </Paper>
    </Container>
  );
};

export default MedicationTrackerPage;
